<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { NIcon, type DropdownOption } from 'naive-ui';
import { IconDelete, IconDownload, IconEdit, IconUpload } from '@/components/design/icons';
import {
  type ContractGenerationTemplateVariantInDb,
  type ContractGenerationTemplateInDb,
  type ContractGenerationTemplateDraftability,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationTemplateQuestionsResponse,
  type ContractGenerationVariantAnswersSummary,
  type TemplateValidationError,
  TaskStatus,
} from '@/api';
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPE_TAG,
  flattenFields,
  type FlattenedField,
} from '@/utils/contractGeneration/templateSchemaFields';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import TemplateVariantUploadModal from '@/components/contractGeneration/templates/TemplateVariantUploadModal.vue';
import TemplatingBreadcrumb from '@/components/contractGeneration/TemplatingBreadcrumb.vue';
import VariantAnswersModal from '@/components/contractGeneration/questions/VariantAnswersModal.vue';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import {
  answerFor,
  describeAnswer,
  draftBlockedMessage,
} from '@/utils/contractGeneration/questions';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaVariantChip from '@/components/design/domain/templating/GaiaVariantChip.vue';
import { templateValidationChipMap } from '@/components/design/statusMaps';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaAccent,
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaFonts,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import { templateRoute as templateDetailRoute } from '@/router/links';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const vaultStore = useVaultStore();

const templateUid = computed<string>(() => {
  const value = route.params.uid;
  return typeof value === 'string' ? value : '';
});

const variantUid = computed<string>(() => {
  const value = route.params.variantUid;
  return typeof value === 'string' ? value : '';
});

const template = ref<ContractGenerationTemplateInDb | null>(null);
const variant = ref<ContractGenerationTemplateVariantInDb | null>(null);
const loading = ref<boolean>(true);
const error = ref<string | null>(null);

// "Upload fixed version" flow — re-opens the existing upload modal pinned to
// this variant so the user can replace the broken .docx.
const replaceUploadVisible = ref<boolean>(false);
const downloadingSource = ref<boolean>(false);

// Delete runs through an inline confirm strip in the hero, not a popconfirm.
const confirmingDelete = ref<boolean>(false);
const deleting = ref<boolean>(false);

// Edit-details modal — variant name + display file name.
const editDetailsVisible = ref<boolean>(false);
const editedName = ref<string>('');
const editedFileName = ref<string>('');
const savingDetails = ref<boolean>(false);

async function load({ silent = false }: { silent?: boolean } = {}): Promise<void> {
  if (!templateUid.value || !variantUid.value) return;
  if (!silent) {
    loading.value = true;
    error.value = null;
  }
  const requestedTemplateUid = templateUid.value;
  const requestedVariantUid = variantUid.value;
  try {
    // The variant is addressed by its own uid, so the detail (with
    // template_status + validation_errors) comes back in one call.
    const [t, detail] = await Promise.all([
      TemplatesService.getById(requestedTemplateUid),
      TemplateVariantsService.get(requestedTemplateUid, requestedVariantUid),
    ]);
    if (requestedTemplateUid !== templateUid.value || requestedVariantUid !== variantUid.value) {
      return;
    }
    template.value = t;
    variant.value = detail;
  } catch (error_) {
    if (requestedTemplateUid !== templateUid.value || requestedVariantUid !== variantUid.value) {
      return;
    }
    if (silent) {
      logger.warn('Background refresh of variant detail failed:', error_);
    } else {
      error.value = error_ instanceof Error ? error_.message : 'Failed to load variant.';
    }
  } finally {
    if (!silent && requestedTemplateUid === templateUid.value) {
      loading.value = false;
    }
  }
}

onMounted(() => {
  void load();
  void loadAnswers();
});

watch([templateUid, variantUid], () => {
  void load();
  void loadAnswers();
});

// ──────────────────────────────
// Answers — what this document covers. Its answers are what a
// person drafting actually picks by; the file name is bookkeeping.
// ──────────────────────────────

const questions = ref<ContractGenerationTemplateQuestionInDb[]>([]);
const summary = ref<ContractGenerationVariantAnswersSummary | null>(null);
const answersLoading = ref<boolean>(true);
const answersError = ref<string | null>(null);
const answersModalOpen = ref<boolean>(false);

/**
 * One row per question, answered or not.
 *
 * Unlike the compact chips on the template page, the detail view lists every
 * question: an author standing on this document needs to see what it *doesn't*
 * narrow on as much as what it does, and here the question's name is beside
 * the value, so "any" reads as an answer rather than as filler.
 */
const answerRows = computed(() =>
  questions.value.map(question => {
    const answer = summary.value ? answerFor(summary.value, question.uid) : null;
    return {
      uid: question.uid,
      question: question.label,
      value: answer ? describeAnswer(answer) : null,
      required: question.required_for_variants,
    };
  })
);

/** Required questions left unanswered — the only kind that blocks drafting. */
const missingRequiredCount = computed<number>(
  () => (summary.value?.unanswered_required_question_uids ?? []).length
);

/**
 * Re-read before opening, rather than trusting what the page loaded.
 *
 * The editor builds one field per question *in the shape that question's type
 * calls for*, and a type is fixed at creation — so the only way it changes is
 * a question being deleted and recreated from the template page, which can
 * happen while this page sits open or in another tab. A stale type sends a
 * chosen option where the backend now expects a band, and it answers with a
 * message naming a question the author is looking at as a choice. One cheap
 * call on the way in removes that whole class of confusion.
 */
async function openAnswers(): Promise<void> {
  await loadAnswers({ silent: true });
  if (summary.value) answersModalOpen.value = true;
}

/** The template's verdict — a document is only draftable if its template is. */
const draftability = ref<ContractGenerationTemplateDraftability | null>(null);

const blockedReason = computed<string | null>(() => draftBlockedMessage(draftability.value));

function adoptQuestionsState(state: ContractGenerationTemplateQuestionsResponse): void {
  questions.value = state.questions ?? [];
  draftability.value = state.draftability;
  summary.value =
    (state.variants ?? []).find(entry => entry.variant_uid === variantUid.value) ?? null;
}

/**
 * Its own read, not part of `load()`: validation polling refreshes that one
 * every 5s while a .docx is being checked, and answers never move underneath
 * it. One call carries the questions and this document's answers together.
 */
async function loadAnswers({ silent = false }: { silent?: boolean } = {}): Promise<void> {
  if (!templateUid.value || !variantUid.value) return;
  const requestedTemplateUid = templateUid.value;
  const requestedVariantUid = variantUid.value;
  if (!silent) {
    answersLoading.value = true;
    answersError.value = null;
  }
  try {
    const state = await TemplateQuestionsService.list(requestedTemplateUid);
    if (requestedTemplateUid !== templateUid.value || requestedVariantUid !== variantUid.value) {
      return;
    }
    adoptQuestionsState(state);
    answersError.value = null;
  } catch (error_) {
    if (requestedTemplateUid !== templateUid.value || requestedVariantUid !== variantUid.value) {
      return;
    }
    // Never fail to an empty list: "this template asks nothing" is a different,
    // valid state, and reading it as that would say the opposite of the truth.
    answersError.value = extractErrorDetail(error_, 'Failed to load this variant’s answers.');
    logger.warn('Failed to load the variant’s answers:', error_);
  } finally {
    if (requestedTemplateUid === templateUid.value && requestedVariantUid === variantUid.value) {
      answersLoading.value = false;
    }
  }
}

// ──────────────────────────────────────────────────────────
// Validation status
// ──────────────────────────────────────────────────────────

const templateStatus = computed<TaskStatus | null>(() => variant.value?.template_status ?? null);

const isValidating = computed<boolean>(
  () => templateStatus.value === TaskStatus.PENDING || templateStatus.value === TaskStatus.RUNNING
);

const hasValidationError = computed<boolean>(() => templateStatus.value === TaskStatus.FAILED);

const isReady = computed<boolean>(() => templateStatus.value === TaskStatus.DONE);

const statusChip = computed(() =>
  templateStatus.value ? templateValidationChipMap[templateStatus.value] : null
);

// Poll the detail endpoint while async validation is in flight, matching the
// 5s cadence used by `GaiaVaultFilePdfPreview` for preview-status checks.
useTemplateValidationPolling(isValidating, () => load({ silent: true }));

const templateRoute = computed<RouteLocationRaw>(() => templateDetailRoute(templateUid.value));

function goBack(): void {
  void router.push(templateRoute.value);
}

function openAuthoring(): void {
  if (!variant.value) return;
  void router.push({
    name: 'contract-generation-template-author',
    params: { uid: templateUid.value, variantUid: variant.value.uid },
  });
}

function startWizard(): void {
  if (!variant.value) return;
  void router.push({
    name: 'contract-generation-wizard',
    params: { templateUid: templateUid.value },
    query: { variant: variant.value.uid },
  });
}

// ──────────────────────────────────────────────────────────
// Edit variant details (name + display file name)
// ──────────────────────────────────────────────────────────

function openEditDetails(): void {
  if (!variant.value) return;
  editedName.value = variant.value.name;
  editedFileName.value = variant.value.file_name;
  editDetailsVisible.value = true;
}

const editDetailsDirty = computed<boolean>(() => {
  if (!variant.value) return false;
  return (
    editedName.value.trim() !== variant.value.name ||
    editedFileName.value.trim() !== variant.value.file_name
  );
});

const canSaveDetails = computed<boolean>(
  () =>
    editDetailsDirty.value &&
    editedName.value.trim().length > 0 &&
    editedFileName.value.trim().length > 0
);

async function saveDetails(): Promise<void> {
  if (!variant.value || !canSaveDetails.value || savingDetails.value) return;
  const payload: { name?: string | null; file_name?: string | null } = {};
  if (editedName.value.trim() !== variant.value.name) {
    payload.name = editedName.value.trim();
  }
  if (editedFileName.value.trim() !== variant.value.file_name) {
    payload.file_name = editedFileName.value.trim();
  }
  savingDetails.value = true;
  try {
    // Addressed by uid, so a rename never invalidates the route we're on.
    // A name another variant of this template already uses is refused by the
    // backend — that message is what the toast shows.
    const updated = await TemplateVariantsService.update(
      templateUid.value,
      variant.value.uid,
      payload
    );
    variant.value = updated;
    editDetailsVisible.value = false;
    message.success('Variant details updated.');
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to update variant details.'));
  } finally {
    savingDetails.value = false;
  }
}

async function deleteVariant(): Promise<void> {
  if (!variant.value) return;
  confirmingDelete.value = false;
  deleting.value = true;
  try {
    await TemplateVariantsService.delete(templateUid.value, variant.value.uid);
    message.success(`Variant “${variant.value.name}” deleted.`);
    void router.push(templateRoute.value);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete variant.'));
  } finally {
    deleting.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// Download source — just the standard vault-revision download
// ──────────────────────────────────────────────────────────

/**
 * The variant row already exposes `vault_file_revision_uid`, so the download
 * is the same flow used by `/vault/file/{uid}/versions`: fetch the
 * revision blob from the vault store, then trigger a synthetic `<a download>`
 * click. No template-specific endpoint required.
 */
async function downloadSource(): Promise<void> {
  if (!variant.value || downloadingSource.value) return;
  const revisionUid = variant.value.vault_file_revision_uid;
  if (!revisionUid) {
    message.warning('No source file is attached to this variant yet.');
    return;
  }
  downloadingSource.value = true;
  try {
    const blob = await vaultStore.downloadFile(
      revisionUid,
      variant.value.mime_type || 'application/octet-stream'
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = variant.value.file_name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to download source file.'));
  } finally {
    downloadingSource.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// Upload fixed version flow
// ──────────────────────────────────────────────────────────

function openReplaceUpload(): void {
  if (!variant.value) return;
  replaceUploadVisible.value = true;
}

function handleReplacedUpload(file: ContractGenerationTemplateVariantInDb): void {
  // Adopt the freshly-uploaded file so the page swaps to its "validating"
  // state immediately. The polling hook on `isValidating` will then refresh
  // until the worker flips status to done/error.
  variant.value = file;
  replaceUploadVisible.value = false;
}

// ──────────────────────────────────────────────────────────
// Header ⋯ menu — the artboard folds every secondary action in here.
// ──────────────────────────────────────────────────────────

function menuIcon(icon: Record<string, unknown>): () => VNode {
  return () => h(NIcon, { size: 14 }, { default: () => h(icon) });
}

const headerMenuOptions = computed<DropdownOption[]>(() => [
  { key: 'upload', label: 'Upload new version', icon: menuIcon(IconUpload) },
  {
    key: 'download',
    label: 'Download',
    icon: menuIcon(IconDownload),
    disabled: !variant.value?.vault_file_revision_uid,
  },
  { key: 'edit', label: 'Edit details', icon: menuIcon(IconEdit) },
  { key: 'divider', type: 'divider' },
  { key: 'delete', label: 'Delete variant', icon: menuIcon(IconDelete) },
]);

function handleHeaderMenu(key: string | number): void {
  switch (String(key)) {
    case 'upload':
      openReplaceUpload();
      return;
    case 'download':
      void downloadSource();
      return;
    case 'edit':
      openEditDetails();
      return;
    case 'delete':
      confirmingDelete.value = true;
      return;
  }
}

// ──────────────────────────────────────────────────────────
// Schema fields
// ──────────────────────────────────────────────────────────

// Siblings follow the order an author set on the Field guidance page.
const flatFields = computed<FlattenedField[]>(() =>
  flattenFields(
    variant.value?.extracted_schema?.fields ?? undefined,
    '',
    0,
    [],
    variant.value?.field_metadata ?? null
  )
);

/**
 * Set of field paths whose children are currently visible. Default state is
 * fully collapsed — the user expands what they want to inspect.
 */
const expandedPaths = ref<Set<string>>(new Set());

watch(
  flatFields,
  () => {
    expandedPaths.value = new Set();
  },
  { immediate: true }
);

function isExpanded(path: string): boolean {
  return expandedPaths.value.has(path);
}

function toggleExpanded(path: string): void {
  const next = new Set(expandedPaths.value);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  expandedPaths.value = next;
}

function expandAll(): void {
  expandedPaths.value = new Set(flatFields.value.filter(f => f.hasChildren).map(f => f.path));
}

function collapseAll(): void {
  expandedPaths.value = new Set();
}

const allExpanded = computed<boolean>(() => {
  const parents = flatFields.value.filter(f => f.hasChildren);
  if (parents.length === 0) return true;
  return parents.every(f => expandedPaths.value.has(f.path));
});

/**
 * Skips rows whose ancestor (at any depth) is currently collapsed.
 *
 * Single-`collapsedAtDepth` tracker is enough because the flat list is in
 * pre-order — once we enter a collapsed subtree, every subsequent row at a
 * deeper depth belongs to it, and the first row at a shallower-or-equal
 * depth means we've left it.
 */
const visibleFields = computed<FlattenedField[]>(() => {
  const out: FlattenedField[] = [];
  let collapsedAtDepth: number | null = null;
  for (const field of flatFields.value) {
    if (collapsedAtDepth !== null && field.depth > collapsedAtDepth) continue;
    collapsedAtDepth = null;
    out.push(field);
    if (field.hasChildren && !expandedPaths.value.has(field.path)) {
      collapsedAtDepth = field.depth;
    }
  }
  return out;
});

const fieldCount = computed<number>(() => flatFields.value.length);

/**
 * Authored labels ride along on the variant detail, keyed by path. A named
 * field shows its name where the tag used to be, with the tag underneath;
 * an unnamed one looks exactly as before.
 */
function authoredLabel(path: string): string | null {
  const label = variant.value?.field_metadata?.[path]?.label?.trim();
  if (!label) return null;
  return label;
}

const requiredFieldCount = computed<number>(() => flatFields.value.filter(f => f.required).length);

/**
 * Async-validation results from the backend worker live on the top-level
 * `validation_errors` column (populated when `template_status === 'failed'`).
 * By backend design, errors are stripped out of `extracted_schema`, so this
 * top-level list is the single source of truth.
 */
const validationErrors = computed<TemplateValidationError[]>(
  () => variant.value?.validation_errors ?? []
);

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// --- chrome ------------------------------------------------------------------
const fsMeta = gaiaFontSize.meta;
const fwSemibold = String(gaiaFontWeight.semibold);
const cardBorder = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const dividerColor = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceSoft;
const dashedBorder = gaiaNeutral.borderDashedSoft;
const accentColor = gaiaAccent.base;
const inkColor = gaiaNeutral.ink;
const subtleColor = gaiaNeutral.textSubtle;
const faintColor = gaiaNeutral.textFaint;
const monoFont = gaiaFonts.mono;
const alertColor = gaiaStatus.alert;
const alertBorder = gaiaStatus.alertBorder;
const alertSurfaceColor = gaiaStatus.alertSurface;
const alertTint = gaiaStatus.alertTint;
const alertText = gaiaStatus.alertText;
const shimmerBase = gaiaNeutral.surfaceMuted;
const shimmerHighlight = gaiaNeutral.surfaceSoft;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.record">
    <template #breadcrumb>
      <templating-breadcrumb
        :template-uid="templateUid"
        :template-name="template?.name"
        :variant-uid="variantUid"
        :variant-name="variant?.name"
      />
    </template>
    <n-flex vertical :size="18">
      <div v-if="loading" class="page-skeleton">
        <div class="shimmer" style="width: 140px" />
        <div class="shimmer tall" style="width: 420px" />
        <div class="shimmer" style="width: 320px" />
        <div class="shimmer block" />
      </div>

      <gaia-empty-state v-else-if="error" status="alert" title="This variant couldn’t be loaded">
        <template #meta>{{ error }}</template>
        <template #actions>
          <gaia-button type="primary" size="small" @click="() => load()">Retry</gaia-button>
          <gaia-button size="small" @click="goBack">Back to template</gaia-button>
        </template>
      </gaia-empty-state>

      <template v-else-if="variant && template">
        <!-- ───────────── Hero card ───────────── -->
        <section class="card hero">
          <n-flex align="flex-start" :size="[20, 16]">
            <n-flex vertical :size="8" :style="{ minWidth: 0, flex: '1 1 380px' }">
              <n-flex align="center" :size="8">
                <gaia-variant-chip :name="variant.name" />
                <gaia-status-chip v-if="statusChip" :tone="statusChip.tone">
                  <gaia-spinner v-if="isValidating" :size="10" />
                  {{ statusChip.label }}
                </gaia-status-chip>
              </n-flex>

              <n-flex align="center" :size="10" :wrap="false" :style="{ minWidth: 0 }">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="hero-file-icon"
                >
                  <path d="M4 2.5h5l3 3v8H4z" />
                  <path d="M9 2.5v3h3" />
                </svg>
                <gaia-header
                  :level="1"
                  variant="display"
                  size="record"
                  :style="{ margin: 0, lineHeight: 1.2, minWidth: 0, overflowWrap: 'break-word' }"
                >
                  {{ variant.file_name }}
                </gaia-header>
              </n-flex>

              <gaia-text size="dense" tone="tertiary">
                Variant of
                <gaia-link :underline="false" :to="templateRoute">{{ template.name }}</gaia-link>
              </gaia-text>

              <n-flex align="center" :size="9" class="hero-meta">
                <gaia-text size="dense" tone="tertiary">
                  {{ fieldCount }} field{{ fieldCount === 1 ? '' : 's' }} extracted
                </gaia-text>
                <template v-if="requiredFieldCount > 0">
                  <span class="meta-dot">·</span>
                  <gaia-text size="dense" tone="tertiary">
                    {{ requiredFieldCount }} required
                  </gaia-text>
                </template>
                <span class="meta-dot">·</span>
                <gaia-text
                  size="dense"
                  tone="tertiary"
                  :style="{ fontVariantNumeric: 'tabular-nums' }"
                >
                  Created {{ formatDate(variant.created_at) }}
                </gaia-text>
                <template v-if="variant.updated_at">
                  <span class="meta-dot">·</span>
                  <gaia-text
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    Updated {{ formatDate(variant.updated_at) }}
                  </gaia-text>
                </template>
              </n-flex>
            </n-flex>

            <n-flex align="center" :size="8" :style="{ marginLeft: 'auto' }">
              <gaia-tooltip :disabled="isReady && blockedReason === null">
                <template #trigger>
                  <span>
                    <gaia-button
                      type="primary"
                      size="medium"
                      :disabled="!isReady || blockedReason !== null"
                      data-tour="tpl.draft-button"
                      @click="startWizard"
                    >
                      <template #icon>
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M8.8 1.8 3.2 9h4l-.8 5.2L12.8 7h-4z" />
                        </svg>
                      </template>
                      Draft document
                    </gaia-button>
                  </span>
                </template>
                <template v-if="isValidating">
                  The template is still validating. Try again in a moment.
                </template>
                <template v-else-if="hasValidationError">
                  Validation failed for this template. Upload a fix before drafting.
                </template>
                <!-- This document may be perfectly fine and still not draftable:
                     the template is held back by one of its others. -->
                <template v-else-if="blockedReason">{{ blockedReason }}</template>
                <template v-else>Template not ready yet.</template>
              </gaia-tooltip>

              <gaia-dropdown
                :options="headerMenuOptions"
                placement="bottom-end"
                @select="handleHeaderMenu"
              >
                <gaia-icon-button :dimension="36" title="More actions" class="menu-button">
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="3.2" cy="8" r="1.3" />
                    <circle cx="8" cy="8" r="1.3" />
                    <circle cx="12.8" cy="8" r="1.3" />
                  </svg>
                </gaia-icon-button>
              </gaia-dropdown>
            </n-flex>
          </n-flex>

          <div v-if="deleting" class="confirm-shell">
            <gaia-table-row-state variant="busy" note="Undo not available">
              Deleting “{{ variant.name }}”…
            </gaia-table-row-state>
          </div>
          <div v-else-if="confirmingDelete" class="confirm-shell">
            <gaia-table-row-state
              variant="confirm"
              :title="`Delete the “${variant.name}” variant?`"
              confirm-label="Delete variant"
              @cancel="confirmingDelete = false"
              @confirm="deleteVariant"
            >
              This cannot be undone. Drafts made from it are deleted too; documents already saved
              are kept.
            </gaia-table-row-state>
          </div>
        </section>

        <!-- ───────────── Validation state ───────────── -->
        <div v-if="hasValidationError" class="failed-card">
          <div class="failed-mark">
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            >
              <path d="M5 5l6 6M11 5l-6 6" />
            </svg>
          </div>
          <n-flex vertical :size="10" :style="{ minWidth: 0, flex: 1 }">
            <n-flex vertical :size="3">
              <gaia-text size="subtitle" weight="semibold" tone="ink">
                Template validation failed
              </gaia-text>
              <gaia-text size="dense" tone="text">
                The uploaded .docx couldn’t be processed. Upload a fixed version, or delete this
                variant and try again.
              </gaia-text>
            </n-flex>
            <ul v-if="validationErrors.length > 0" class="failed-list">
              <li v-for="(item, idx) in validationErrors" :key="`banner-${idx}`">
                {{ item.message }}
              </li>
            </ul>
            <n-flex align="center" :size="12">
              <gaia-button type="primary" size="small" @click="openReplaceUpload">
                Upload fixed version
              </gaia-button>
              <gaia-delete-button
                variant="label"
                subject="variant"
                @click="confirmingDelete = true"
              />
            </n-flex>
          </n-flex>
        </div>

        <gaia-banner v-else-if="isValidating" type="info" title="Validating template…">
          We’re checking the uploaded .docx. This usually takes just a few seconds — the page
          refreshes automatically once validation finishes.
        </gaia-banner>

        <!-- ───────────── Answers ───────────── -->
        <section class="card flush">
          <n-flex align="flex-start" :size="10" class="card-head">
            <n-flex vertical :size="3" :style="{ minWidth: 0 }">
              <n-flex align="baseline" :size="8">
                <gaia-text size="subtitle" weight="semibold" tone="ink">Answers</gaia-text>
                <gaia-count-pill v-if="questions.length > 0">{{
                  questions.length
                }}</gaia-count-pill>
              </n-flex>
              <gaia-text size="dense" tone="tertiary">
                What this variant covers. Someone drafting answers these questions, and this
                document is one of the candidates their answers leave standing.
              </gaia-text>
            </n-flex>
            <gaia-button
              v-if="questions.length > 0 && summary && !answersLoading"
              size="small"
              :style="{ marginLeft: 'auto' }"
              @click="openAnswers"
            >
              Edit answers
            </gaia-button>
          </n-flex>

          <div v-if="missingRequiredCount > 0" class="answers-note">
            <gaia-banner
              type="warning"
              :title="`${missingRequiredCount} required question${missingRequiredCount === 1 ? '' : 's'} unanswered`"
            >
              A question marked “every document must answer” is still blank here. Until it is
              answered, the whole template can’t be drafted from.
            </gaia-banner>
          </div>

          <div v-if="answersLoading" class="answers-body">
            <div class="shimmer" style="width: 260px" />
          </div>

          <n-flex v-else-if="answersError" align="center" :size="8" class="answers-body">
            <gaia-status-indicator status="alert" />
            <gaia-text size="dense" tone="alert">{{ answersError }}</gaia-text>
            <gaia-link :underline="false" @click="() => loadAnswers()">Retry</gaia-link>
          </n-flex>

          <div v-else-if="questions.length === 0" class="answers-body">
            <gaia-empty-state dashed title="This template asks no questions">
              With one document there is nothing to choose between. Add a question on the template
              as soon as there is more than one — it is what tells them apart.
              <template #actions>
                <gaia-button size="small" @click="goBack">Open the template</gaia-button>
              </template>
            </gaia-empty-state>
          </div>

          <ul v-else class="answer-list">
            <li v-for="row in answerRows" :key="row.uid" class="answer-row">
              <gaia-text size="dense" weight="semibold" tone="ink" :style="{ minWidth: 0 }">
                {{ row.question }}
              </gaia-text>
              <div :style="{ marginLeft: 'auto', flex: 'none' }">
                <gaia-tag v-if="row.value" size="small" round>{{ row.value }}</gaia-tag>
                <n-flex v-else-if="row.required" align="center" :size="6" :wrap="false">
                  <gaia-status-indicator status="alert" />
                  <gaia-text size="meta" tone="alert">Not answered</gaia-text>
                </n-flex>
                <!-- Unanswered and optional: it widens rather than excludes,
                     so it is an answer of its own, not a gap. -->
                <gaia-text v-else size="meta" tone="subtle">Any value</gaia-text>
              </div>
            </li>
          </ul>
        </section>

        <!-- ───────────── Two-column body ───────────── -->
        <div class="body-grid">
          <!-- Source preview -->
          <section class="card flush">
            <n-flex vertical :size="3" class="card-head">
              <gaia-text size="subtitle" weight="semibold" tone="ink">Source document</gaia-text>
              <gaia-text size="dense" tone="tertiary">
                The underlying .docx the wizard uses as a base.
              </gaia-text>
            </n-flex>
            <div v-if="variant.vault_file_uid" class="preview-body">
              <gaia-vault-file-pdf-preview
                :vault-file-uid="variant.vault_file_uid"
                :revision-uid="variant.vault_file_revision_uid ?? undefined"
              />
            </div>
            <gaia-empty-state v-else title="No source file is attached to this variant" />
          </section>

          <!-- Schema fields -->
          <section class="card flush" data-tour="tpl.fields">
            <n-flex align="flex-start" :size="10" class="card-head">
              <n-flex vertical :size="3" :style="{ minWidth: 0 }">
                <n-flex align="baseline" :size="8">
                  <gaia-text size="subtitle" weight="semibold" tone="ink">
                    Extracted fields
                  </gaia-text>
                  <gaia-count-pill>{{ fieldCount }}</gaia-count-pill>
                </n-flex>
                <gaia-text size="dense" tone="tertiary">
                  Placeholders detected in the source. Configure them and write guidance for the
                  people filling them in with Edit fields.
                </gaia-text>
              </n-flex>
              <n-flex align="center" :size="8" :wrap="false" :style="{ marginLeft: 'auto' }">
                <gaia-button
                  v-if="flatFields.some(f => f.hasChildren)"
                  size="small"
                  quaternary
                  @click="allExpanded ? collapseAll() : expandAll()"
                >
                  {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                </gaia-button>
                <gaia-button size="small" data-tour="tpl.edit-fields" @click="openAuthoring">
                  <template #icon>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M3 13.2 12.2 4l-1.7-1.7L1.3 11.5 1 14z" />
                    </svg>
                  </template>
                  Edit fields
                </gaia-button>
              </n-flex>
            </n-flex>

            <gaia-empty-state v-if="fieldCount === 0" title="No fields detected yet">
              Open the field editor to map placeholders.
            </gaia-empty-state>

            <ul v-else class="field-list">
              <li
                v-for="field in visibleFields"
                :key="field.path"
                class="field-row"
                :class="{ expandable: field.hasChildren }"
                :style="{ paddingLeft: `${18 + field.depth * 18}px` }"
                @click="field.hasChildren && toggleExpanded(field.path)"
              >
                <svg
                  v-if="field.hasChildren"
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  class="field-chevron"
                  :class="{ open: isExpanded(field.path) }"
                >
                  <path d="M4.5 2.5 8 6l-3.5 3.5" />
                </svg>
                <span v-else class="field-chevron placeholder" />
                <n-flex vertical :size="2" :style="{ minWidth: 0 }">
                  <template v-if="authoredLabel(field.path)">
                    <gaia-text size="dense" weight="semibold" tone="ink">
                      {{ authoredLabel(field.path) }}
                    </gaia-text>
                    <span class="field-path">{{ field.path }}</span>
                  </template>
                  <template v-else>
                    <span class="field-name">{{ field.key }}</span>
                    <span v-if="field.depth > 0" class="field-path">{{ field.path }}</span>
                  </template>
                </n-flex>
                <n-flex
                  align="center"
                  :size="6"
                  :wrap="false"
                  :style="{ marginLeft: 'auto', flex: 'none' }"
                >
                  <gaia-tag size="small" :type="FIELD_TYPE_TAG[field.type] ?? 'default'" round>
                    {{ FIELD_TYPE_LABEL[field.type] ?? field.type }}
                    <template v-if="field.type === 'list' && field.itemType">
                      &lt;{{ FIELD_TYPE_LABEL[field.itemType] ?? field.itemType }}&gt;
                    </template>
                  </gaia-tag>
                  <gaia-tag v-if="field.required" size="small" type="error" round>
                    Required
                  </gaia-tag>
                  <gaia-tooltip v-if="field.defaultValue !== null">
                    <template #trigger>
                      <gaia-tag size="small" round>Default</gaia-tag>
                    </template>
                    Default: {{ field.defaultValue }}
                  </gaia-tooltip>
                  <gaia-tooltip v-if="field.selectionOptions.length > 0">
                    <template #trigger>
                      <gaia-tag size="small" round>
                        {{ field.selectionOptions.length }} options
                      </gaia-tag>
                    </template>
                    {{ field.selectionOptions.join(', ') }}
                  </gaia-tooltip>
                  <gaia-tooltip v-if="field.filters.length > 0">
                    <template #trigger>
                      <gaia-tag size="small" round>
                        {{ field.filters.length }} filter{{ field.filters.length === 1 ? '' : 's' }}
                      </gaia-tag>
                    </template>
                    {{ field.filters.join(' | ') }}
                  </gaia-tooltip>
                </n-flex>
              </li>
            </ul>
          </section>
        </div>

        <variant-answers-modal
          v-if="summary"
          :show="answersModalOpen"
          :template-uid="templateUid"
          :variant="summary"
          :questions="questions"
          @update:show="(value: boolean) => (answersModalOpen = value)"
          @saved="adoptQuestionsState"
        />

        <TemplateVariantUploadModal
          v-model:show="replaceUploadVisible"
          :template-uid="templateUid"
          :existing-names="[]"
          :variant-uid="variant.uid"
          :locked-variant-name="variant.name"
          @uploaded="handleReplacedUpload"
        />
      </template>
    </n-flex>

    <gaia-modal
      v-model:show="editDetailsVisible"
      width="480px"
      :mask-closable="!savingDetails"
      :closable="!savingDetails"
    >
      <template #header>
        <gaia-text size="subtitle" weight="semibold" tone="ink">Edit variant details</gaia-text>
      </template>
      <n-flex vertical :size="14">
        <gaia-form-field label="Variant name" required hint="a language, a country, a wording">
          <gaia-input
            v-model:value="editedName"
            placeholder="e.g. German, Freelancer (Germany)"
            :disabled="savingDetails"
            style="width: 100%"
          />
        </gaia-form-field>
        <gaia-form-field label="File name" required>
          <gaia-input
            v-model:value="editedFileName"
            placeholder="my-template.docx"
            :disabled="savingDetails"
            style="width: 100%"
          />
        </gaia-form-field>
      </n-flex>
      <template #footer>
        <n-flex justify="end" :size="8">
          <gaia-button size="small" :disabled="savingDetails" @click="editDetailsVisible = false">
            Cancel
          </gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :loading="savingDetails"
            :disabled="!canSaveDetails"
            @click="saveDetails"
          >
            Save
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>

<style scoped>
.page-skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 6px 0;
}

.shimmer {
  height: 11px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerBase) 0%,
    v-bind(shimmerHighlight) 45%,
    v-bind(shimmerBase) 90%
  );
  background-size: 480px 100%;
  animation: variant-detail-shimmer 1.5s linear infinite;
}

.shimmer.tall {
  height: 26px;
  border-radius: 6px;
}

.shimmer.block {
  height: 220px;
  border-radius: 12px;
  margin-top: 14px;
}

@keyframes variant-detail-shimmer {
  from {
    background-position: -480px 0;
  }
  to {
    background-position: 480px 0;
  }
}

.card {
  display: flex;
  flex-direction: column;
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  background: v-bind(surface);
}

.hero {
  gap: 14px;
  padding: 20px 22px;
}

.hero-file-icon {
  flex: none;
  color: v-bind(accentColor);
}

.hero-meta {
  margin-top: 2px;
}

.meta-dot {
  color: v-bind(dashedBorder);
}

.menu-button {
  border: 1px solid v-bind(cardBorder);
  color: v-bind(subtleColor);
}

.confirm-shell {
  border: 1px solid v-bind(alertBorder);
  border-radius: 10px;
  overflow: hidden;
  background: v-bind(alertSurfaceColor);
}

/* The failed-validation card: the artboard's rich alert, not a slim banner —
   it carries the worker's error list and the two recovery actions. */
.failed-card {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid v-bind(alertBorder);
  border-radius: 12px;
  background: v-bind(alertSurfaceColor);
}

.failed-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  margin-top: 1px;
  border-radius: 50%;
  background: v-bind(alertTint);
  color: v-bind(alertColor);
}

.failed-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 12px 14px 12px 28px;
  border: 1px solid v-bind(alertBorder);
  border-radius: 9px;
  background: v-bind(alertTint);
  font-size: v-bind(fsMeta);
  line-height: 1.55;
  color: v-bind(alertText);
}

/* One row per question, edge to edge in a flush card — the same list shape as
   the field list below it. */
.answer-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid v-bind(dividerColor);
}

.answer-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border-bottom: 1px solid v-bind(dividerColor);
}

.answer-row:last-child {
  border-bottom: none;
}

/* Card body that isn't the edge-to-edge list: banner, empty state, skeleton. */
.answers-note {
  padding: 0 18px 14px;
}

.answers-body {
  padding: 0 18px 18px;
}

/* Preview left, fields right — the mock's two uneven tracks. */
.body-grid {
  display: grid;
  grid-template-columns: minmax(340px, 1fr) minmax(380px, 1.1fr);
  gap: 18px;
  align-items: start;
}

@media (max-width: 1100px) {
  .body-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards whose body owns its edges (field rows, PDF panel). */
.card.flush {
  overflow: hidden;
}

.card-head {
  padding: 16px 18px 12px;
}

/* A definite height, not min/max: the PDF preview is a flex column whose body
   scrolls in "the remaining height", and against an auto-height wrapper there
   is no remaining height — the body grows to the whole document, the card
   clips it, and the page tracker (which reads a quarter of the way down what
   it takes for the viewport) settles on a page in the middle and leaves the
   visible first page as a blank spacer. Matches the field list's 640px cap. */
.preview-body {
  border-top: 1px solid v-bind(dividerColor);
  height: 640px;
}

.field-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid v-bind(dividerColor);
  max-height: 640px;
  overflow-y: auto;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 18px;
  border-bottom: 1px solid v-bind(dividerColor);
}

.field-row:last-child {
  border-bottom: none;
}

.field-row.expandable {
  cursor: pointer;
}

.field-row.expandable:hover {
  background: v-bind(rowHover);
}

.field-chevron {
  flex: none;
  width: 10px;
  color: v-bind(subtleColor);
  transition: transform 0.15s;
}

.field-chevron.open {
  transform: rotate(90deg);
}

.field-name {
  font-family: v-bind(monoFont);
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  color: v-bind(inkColor);
}

.field-path {
  font-family: v-bind(monoFont);
  font-size: v-bind(fsMeta);
  color: v-bind(faintColor);
}
</style>
